import { Box, CircularProgress, Typography } from '@gapartners/ui';
import type { CustomerCardBodyProps } from './types';

function renderNoPhoneContent({
  isDirectConnect,
  isAuthenticated,
  contactId,
  customerExists,
  directConnectContent,
  t,
}: Pick<
  CustomerCardBodyProps,
  | 'isDirectConnect'
  | 'isAuthenticated'
  | 'contactId'
  | 'customerExists'
  | 'directConnectContent'
  | 't'
>) {
  if ((isDirectConnect || !isAuthenticated) && contactId && !customerExists) {
    return directConnectContent;
  }

  if (customerExists) {
    return null;
  }

  return (
    <Typography variant="body2" color="text.secondary">
      {t('components.contactCenter.noCustomerData')}
    </Typography>
  );
}

export function renderCustomerCardBody({
  callerPhoneNumber,
  isDirectConnect,
  isAuthenticated,
  contactId,
  directConnectLinkedWorkspaceId,
  directConnectLinkedCompanyName,
  isLoading,
  error,
  customerExists,
  t,
  directConnectContent,
}: CustomerCardBodyProps) {
  const isDirectLinked = Boolean(
    isDirectConnect && contactId && directConnectLinkedWorkspaceId
  );

  if (isDirectLinked) {
    if (isLoading) {
      return (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: 128,
          }}
        >
          <CircularProgress />
        </Box>
      );
    }

    if (error) {
      return (
        <Typography variant="body2" color="error.main">
          {t('components.contactCenter.errorLoadingCustomerInfo')}
        </Typography>
      );
    }

    if (!customerExists) {
      return (
        <Typography variant="body2" color="text.secondary">
          {t('components.contactCenter.directConnectLinkedDescription', {
            name: directConnectLinkedCompanyName ?? '—',
          })}
        </Typography>
      );
    }

    return null;
  }

  if (!callerPhoneNumber?.trim()) {
    const noPhoneContent = renderNoPhoneContent({
      isDirectConnect,
      isAuthenticated,
      contactId,
      customerExists,
      directConnectContent,
      t,
    });
    if (!noPhoneContent) return null;
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {noPhoneContent}
      </Box>
    );
  }

  if ((isDirectConnect || !isAuthenticated) && contactId && !customerExists) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {directConnectContent}
      </Box>
    );
  }

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: 128,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Typography variant="body2" color="error.main">
        {t('components.contactCenter.errorLoadingCustomerInfo')}
      </Typography>
    );
  }

  if (!customerExists) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Typography variant="body2" color="text.secondary">
          {t('components.contactCenter.noCustomerData')}
        </Typography>
        {directConnectContent}
      </Box>
    );
  }

  return null;
}
